import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { queryKeys } from '@/lib/queryKeys'
import { useAuthStore } from '@/store/authStore'
import { extractApiErrorMessage } from '@/utils/apiError'
import {
  orderWebhooksApi,
  type WebhookDeliveryStatus,
  type WebhookEndpointInput,
} from '@/services/orderWebhooksApi'

export function useWebhookEndpoints() {
  const tenantId = useAuthStore((state) => state.tenantId)

  return useQuery({
    queryKey: queryKeys.webhookEndpoints(tenantId),
    queryFn: () => orderWebhooksApi.listEndpoints(),
    enabled: Boolean(tenantId),
    staleTime: 15_000,
  })
}

export function useWebhookDeliveries(endpointId: string | null, status?: WebhookDeliveryStatus) {
  const tenantId = useAuthStore((state) => state.tenantId)

  return useQuery({
    queryKey: queryKeys.webhookDeliveries(tenantId, endpointId ?? 'none', status),
    queryFn: () => orderWebhooksApi.listDeliveries(endpointId as string, { status, limit: 30 }),
    enabled: Boolean(tenantId && endpointId),
    staleTime: 10_000,
  })
}

function useInvalidateEndpoints() {
  const queryClient = useQueryClient()
  const tenantId = useAuthStore((state) => state.tenantId)

  return () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.webhookEndpoints(tenantId) })
}

export function useCreateWebhookEndpoint() {
  const invalidate = useInvalidateEndpoints()

  return useMutation({
    mutationFn: (input: WebhookEndpointInput) => orderWebhooksApi.createEndpoint(input),
    onSuccess: () => void invalidate(),
    onError: (error) => toast.error(extractApiErrorMessage(error, 'No se pudo crear el destino').message),
  })
}

export function useUpdateWebhookEndpoint() {
  const invalidate = useInvalidateEndpoints()

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<WebhookEndpointInput> }) =>
      orderWebhooksApi.updateEndpoint(id, input),
    onSuccess: () => {
      void invalidate()
      toast.success('Destino actualizado')
    },
    onError: (error) => toast.error(extractApiErrorMessage(error, 'No se pudo actualizar el destino').message),
  })
}

export function useDeleteWebhookEndpoint() {
  const invalidate = useInvalidateEndpoints()

  return useMutation({
    mutationFn: (id: string) => orderWebhooksApi.deleteEndpoint(id),
    onSuccess: () => {
      void invalidate()
      toast.success('Destino eliminado')
    },
    onError: (error) => toast.error(extractApiErrorMessage(error, 'No se pudo eliminar el destino').message),
  })
}

export function useRotateWebhookSecret() {
  const invalidate = useInvalidateEndpoints()

  return useMutation({
    mutationFn: (id: string) => orderWebhooksApi.rotateSecret(id),
    onSuccess: () => void invalidate(),
    onError: (error) => toast.error(extractApiErrorMessage(error, 'No se pudo rotar el secreto').message),
  })
}

export function useTestWebhookEndpoint() {
  return useMutation({
    mutationFn: (id: string) => orderWebhooksApi.testEndpoint(id),
    onError: (error) => toast.error(extractApiErrorMessage(error, 'No se pudo enviar la prueba').message),
  })
}

export function useRedeliverWebhook() {
  const queryClient = useQueryClient()
  const tenantId = useAuthStore((state) => state.tenantId)

  return useMutation({
    mutationFn: (deliveryId: string) => orderWebhooksApi.redeliver(deliveryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-deliveries', tenantId ?? 'no-tenant'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.webhookEndpoints(tenantId) })
      toast.success('Reenvío programado')
    },
    onError: (error) => toast.error(extractApiErrorMessage(error, 'No se pudo reenviar el aviso').message),
  })
}
